import React, { useState, useEffect } from "react";
import {
  Box, Typography, Button, Dialog, DialogTitle, DialogContent,
  TextField, IconButton, Grid, Stack, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Paper,
  TablePagination, Divider, Chip, MenuItem, CircularProgress
} from "@mui/material";
import {
  Add as AddIcon, Search as SearchIcon, Edit as EditIcon,
  Delete as DeleteIcon, Close as CloseIcon, DirectionsCar as CarIcon,
  CalendarMonth as CalendarIcon
} from "@mui/icons-material";
import { db } from "../../firebase";
import {
  collection, addDoc, getDocs, query, orderBy, doc, updateDoc, deleteDoc, Timestamp
} from "firebase/firestore";
import Swal from "sweetalert2";

const PRIMARY = "#0d9488";

function Vehicalmgmt() {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);

  // Search & Pagination State
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [form, setForm] = useState({
    vehicleNumber: "",
    ownerName: "",
    authority: "",
    vehicleClass: "",
    fuelType: "",
    emissionNorm: "",
    vehicleAge: "",
    hypothecated: "No",
    status: "",
    registrationDate: "",
    fitnessValidUpto: "",
    taxValidUpto: "LTT",
    insuranceValidUpto: "",
    puccValidUpto: ""
  });

  useEffect(() => { fetchVehicles(); }, []);

  const fetchVehicles = async () => {
    setLoading(true);
    try {
      const q = query(collection(db, "vehicles"), orderBy("createdAt", "desc"));
      const snap = await getDocs(q);
      setVehicles(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const handleSubmit = async () => {
    if (!form.vehicleNumber || !form.ownerName) {
      return Swal.fire("Error", "Vehicle Number and Owner Name are required", "error");
    }
    setLoading(true);
    try {
      if (editId) {
        await updateDoc(doc(db, "vehicles", editId), { ...form, updatedAt: Timestamp.now() });
        Swal.fire("Updated", "Vehicle record updated successfully", "success");
      } else {
        await addDoc(collection(db, "vehicles"), { ...form, createdAt: Timestamp.now() });
        Swal.fire("Saved", "New vehicle added to fleet", "success");
      }
      setOpen(false);
      resetForm();
      fetchVehicles();
    } catch (err) { Swal.fire("Error", err.message, "error"); }
    setLoading(false);
  };

  const handleDelete = async (id) => {
    const confirm = await Swal.fire({
      title: "Delete Vehicle?",
      text: "This will permanently remove the record from Firebase.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33"
    });
    if (confirm.isConfirmed) {
      await deleteDoc(doc(db, "vehicles", id));
      fetchVehicles();
      Swal.fire("Deleted", "Record removed", "success");
    }
  };

  const handleEdit = (row) => {
    setEditId(row.id);
    setForm({ ...row });
    setOpen(true);
  };

  const resetForm = () => {
    setEditId(null);
    setForm({
      vehicleNumber: "", ownerName: "", authority: "SANGLI, Maharashtra",
      vehicleClass: "Motor Car(LMV)", fuelType: "DIESEL", emissionNorm: "Not Available",
      vehicleAge: "", hypothecated: "No", status: "ACTIVE", registrationDate: "",
      fitnessValidUpto: "", taxValidUpto: "LTT", insuranceValidUpto: "", puccValidUpto: ""
    });
  };

  const filteredData = vehicles.filter(v =>
    v.vehicleNumber.toLowerCase().includes(search.toLowerCase()) ||
    v.ownerName.toLowerCase().includes(search.toLowerCase())
  );

  const calculateVehicleAge = (regDate) => {
    if (!regDate) return "N/A";

    const today = new Date();
    const birthDate = new Date(regDate);

    let years = today.getFullYear() - birthDate.getFullYear();
    let months = today.getMonth() - birthDate.getMonth();
    let days = today.getDate() - birthDate.getDate();

    // If days are negative, borrow from the previous month
    if (days < 0) {
      months -= 1;
      // Get the last day of the previous month
      const previousMonth = new Date(today.getFullYear(), today.getMonth(), 0);
      days += previousMonth.getDate();
    }

    // If months are negative, borrow from the year
    if (months < 0) {
      years -= 1;
      months += 12;
    }

    // Formatting the string
    const yPart = years > 0 ? `${years}y ` : "";
    const mPart = months > 0 ? `${months}m ` : "";
    const dPart = `${days}d`;

    return `${yPart}${mPart}${dPart}`.trim();
  };
  return (
    <Box p={1} sx={{ background: "#f8fafc", minHeight: "100vh" }}>
      <Stack direction="row" justifyContent="space-between" mb={3}>
        <Typography variant="h6" fontWeight="bold">Vehicle Management (RC Details)</Typography>
        <Button variant="contained" startIcon={<AddIcon />} sx={{ bgcolor: PRIMARY }} onClick={() => { resetForm(); setOpen(true); }}>
          Add Vehicle
        </Button>
      </Stack>

      <Paper sx={{ borderRadius: 2, overflow: "hidden" }} elevation={0}>
        <Box p={2}>
          <TextField
            fullWidth size="small" placeholder="Search by number or owner..."
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1, color: "gray" }} /> }}
          />
        </Box>

        <TableContainer>
          <Table size="small">
            <TableHead sx={{ bgcolor: "#f1f5f9" }}>
              <TableRow>
                <TableCell><b>Sr.</b></TableCell>
                <TableCell><b>Vehicle Number</b></TableCell>
                <TableCell><b>Owner & Class</b></TableCell>
                <TableCell><b>Vehicle Age</b></TableCell> {/* Added this */}
                <TableCell><b>Insurance & PUCC</b></TableCell>
                <TableCell><b>Fitness</b></TableCell>
                <TableCell><b>Status</b></TableCell>
                <TableCell align="center"><b>Actions</b></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? <TableRow><TableCell colSpan={7} align="center"><CircularProgress size={24} /></TableCell></TableRow> :
                filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row, index) => (
                  <TableRow key={row.id} hover>
                    <TableCell>{page * rowsPerPage + index + 1}</TableCell>

                    <TableCell>
                      <Stack spacing={0.5}>
                        <Typography variant="body2" fontWeight="bold">{row.vehicleNumber}</Typography>
                        <Typography variant="caption" color="text.secondary">Reg: {row.registrationDate}</Typography>
                      </Stack>
                    </TableCell>

                    <TableCell>
                      {row.ownerName}
                      <br />
                      <Typography variant="caption" color="text.secondary">{row.vehicleClass}</Typography>
                    </TableCell>

                    {/* AUTOMATIC AGE DISPLAY */}
                    <TableCell>
                      <Chip
                        label={calculateVehicleAge(row.registrationDate)}
                        size="small"
                        sx={{ fontSize: '0.7rem', fontWeight: 600, bgcolor: '#f0fdfa', color: PRIMARY }}
                      />
                    </TableCell>

                    <TableCell>
                      <Typography variant="caption" display="block">INS: {row.insuranceValidUpto}</Typography>
                      <Typography variant="caption" display="block">PUCC: {row.puccValidUpto}</Typography>
                    </TableCell>

                    <TableCell>
                      <Typography variant="caption">Valid till: {row.fitnessValidUpto}</Typography>
                    </TableCell>

                    <TableCell>
                      <Chip
                        label={row.status}
                        size="small"
                        color={row.status === "ACTIVE" ? "success" : "error"}
                        variant="outlined"
                      />
                    </TableCell>

                    <TableCell align="center">
                      <IconButton size="small" onClick={() => handleEdit(row)} color="primary">
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDelete(row.id)} color="error">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination component="div" count={filteredData.length} rowsPerPage={rowsPerPage} page={page} onPageChange={(e, n) => setPage(n)} />
      </Paper>

      {/* DIALOG FORM WITH ALL PHOTO FIELDS */}
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="md">
        <DialogTitle sx={{ fontWeight: "bold" }}>{editId ? "Update RC Details" : "New Vehicle Registration"}</DialogTitle>
        <Divider />
        <DialogContent>
          <Grid className="row" spacing={2} mt={1}>
            <Grid className="col-sm-4 mb-3" item xs={12} sm={4}><TextField label="Vehicle Number" fullWidth size="small" value={form.vehicleNumber} onChange={(e) => setForm({ ...form, vehicleNumber: e.target.value.toUpperCase() })} /></Grid>
            <Grid className="col-sm-4 mb-3" item xs={12} sm={4}><TextField label="Owner Name" fullWidth size="small" value={form.ownerName} onChange={(e) => setForm({ ...form, ownerName: e.target.value })} /></Grid>
            <Grid className="col-sm-4 mb-3" item xs={12} sm={4}><TextField label="Registration Date" type="date" fullWidth size="small" InputLabelProps={{ shrink: true }} value={form.registrationDate} onChange={(e) => setForm({ ...form, registrationDate: e.target.value })} /></Grid>

            <Grid className="col-sm-4 mb-3" item xs={12} sm={6}><TextField label="Registering Authority" fullWidth size="small" value={form.authority} onChange={(e) => setForm({ ...form, authority: e.target.value })} /></Grid>
            <Grid className="col-sm-4 mb-3" item xs={12} sm={6}><TextField label="Vehicle Class" fullWidth size="small" value={form.vehicleClass} onChange={(e) => setForm({ ...form, vehicleClass: e.target.value })} /></Grid>

            <Grid className="col-sm-4 mb-3" item xs={12} sm={3}>
              <TextField select label="Fuel Type" fullWidth size="small" value={form.fuelType} onChange={(e) => setForm({ ...form, fuelType: e.target.value })}>
                {["DIESEL", "PETROL", "CNG", "ELECTRIC"].map(o => <MenuItem key={o} value={o}>{o}</MenuItem>)}
              </TextField>
            </Grid>

            <Grid className="col-sm-12 mb-3" item xs={12}><Divider><Typography variant="caption" fontWeight="bold">VALIDITY DATES</Typography></Divider></Grid>

            <Grid className="col-sm-4 mb-3" item xs={12} sm={4}><TextField label="Fitness Valid Upto" type="date" fullWidth size="small" InputLabelProps={{ shrink: true }} value={form.fitnessValidUpto} onChange={(e) => setForm({ ...form, fitnessValidUpto: e.target.value })} /></Grid>
            <Grid className="col-sm-4 mb-3" item xs={12} sm={4}><TextField label="Insurance Valid Upto" type="date" fullWidth size="small" InputLabelProps={{ shrink: true }} value={form.insuranceValidUpto} onChange={(e) => setForm({ ...form, insuranceValidUpto: e.target.value })} /></Grid>
            <Grid className="col-sm-4 mb-3" item xs={12} sm={4}><TextField label="PUCC Valid Upto" type="date" fullWidth size="small" InputLabelProps={{ shrink: true }} value={form.puccValidUpto} onChange={(e) => setForm({ ...form, puccValidUpto: e.target.value })} /></Grid>

            <Grid className="col-sm-4 mb-3" item xs={12} sm={6}><TextField label="Tax Valid Upto" placeholder="LTT" fullWidth size="small" value={form.taxValidUpto} onChange={(e) => setForm({ ...form, taxValidUpto: e.target.value })} /></Grid>
            <Grid className="col-sm-4 mb-3" item xs={12} sm={6}>
              <TextField select label="Vehicle Status" fullWidth size="small" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                <MenuItem value="ACTIVE">ACTIVE</MenuItem>
                <MenuItem value="INACTIVE">INACTIVE</MenuItem>
              </TextField>
            </Grid>
          </Grid>

          <Button variant="contained" fullWidth sx={{ mt: 3, py: 1.5, bgcolor: PRIMARY }} onClick={handleSubmit} disabled={loading}>
            {loading ? <CircularProgress size={24} /> : (editId ? "Update Vehicle Record" : "Save ")}
          </Button>
        </DialogContent>
      </Dialog>
    </Box>
  );
}

export default Vehicalmgmt;